import type {User} from "@irati/types"
import {DistributeRewardRecipient, DistributeRewardImportType} from "../../enums/DistributeRewardTypes"
import type {DistributeRewardActionTypes, DistributeRewardState} from "./actions"

const DistributeRewardReducer = (state: DistributeRewardState, action: DistributeRewardActionTypes): DistributeRewardState => {
  switch (action.type) {
    case "SWITCH_RECIPIENT": {
      return {
        ...state,
        userEmails: [],
        destinationUsers: [],
        recipientType: action.payload.recipientType,
        rewardTemplate: action.payload.recipientType === DistributeRewardRecipient.MANAGER ? null : state.rewardTemplate,
        rewardImportType: DistributeRewardImportType.MANUAL_ENTER,
        bulkRewardState: {
          ...state.bulkRewardState,
          csvError: null
        },
        bulkBudgetAssignmentState: {
          ...state.bulkBudgetAssignmentState,
          assignments: [],
          totalToPay: 0,
          failedEmails: []
        },
        errors: {
          ...state.errors,
          noRecipientType: false
        }
      }
    }
    case "SWITCH_IMPORT_TYPE": {
      return {
        ...state,
        userEmails: [],
        destinationUsers: [],
        rewardImportType: action.payload.importType
      }
    }
    case "UPDATE_RECIPIENTS": {
      return {
        ...state,
        destinationUsers:
          state.rewardImportType === DistributeRewardImportType.MANUAL_ENTER ? (action.payload.recipientsList as User[]) : [],
        userEmails: state.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? (action.payload.recipientsList as string[]) : []
      }
    }
    case "SET_USER_POINTS": {
      return {
        ...state,
        pointsPerUser: parseInt(action.payload.userPoints, 10)
      }
    }
    case "SET_MESSAGE": {
      return {
        ...state,
        message: action.payload.message,
        recognition: {
          ...state.recognition,
          message: action.payload.message
        }
      }
    }
    case "SET_RECOGNITION": {
      return {
        ...state,
        recognition: {...state.recognition, ...action.payload.recognition}
      }
    }
    case "SET_BULK_BUDGET_ASSIGNMENT_STATE": {
      return {
        ...state,
        bulkBudgetAssignmentState: {
          ...state.bulkBudgetAssignmentState,
          ...action.payload
        }
      }
    }
    case "SET_BULK_REWARD_STATE": {
      return {
        ...state,
        bulkRewardState: {...state.bulkRewardState, ...action.payload}
      }
    }
    case "SET_MODAL_ERROR_STATE": {
      return {
        ...state,
        modalErrorState: action.payload
      }
    }
    case "ON_ACCEPT_EMAIL_ERRORS": {
      const isValidEmail = (email: string) => {
        const failedEmailsList =
          state.recipientType === DistributeRewardRecipient.MANAGER
            ? state.bulkBudgetAssignmentState.failedEmails
            : state.bulkRewardState.failedEmails

        return !failedEmailsList?.find(({email: failedEmail}) => failedEmail === email)
      }
      const validEmails = state.userEmails.filter(isValidEmail)
      let totalToPay = 0
      if (state.recipientType === DistributeRewardRecipient.MANAGER && state.rewardImportType === DistributeRewardImportType.IMPORT_CSV) {
        // Recalculate the total points to pay based on valid emails
        totalToPay = state.bulkBudgetAssignmentState.assignments.reduce((acc, assignment) => {
          return (
            acc +
            assignment.users.reduce((sum, user) => {
              if (validEmails.includes(user.email)) {
                return sum + user.amount
              }
              return sum
            }, 0)
          )
        }, 0)
      }
      return {
        ...state,
        step: "message",
        userEmails: validEmails,
        bulkRewardState: {
          ...state.bulkRewardState,
          failedEmails: []
        },
        bulkBudgetAssignmentState: {
          ...state.bulkBudgetAssignmentState,
          totalToPay,
          failedEmails: []
        }
      }
    }
    case "ON_REJECT_EMAIL_ERRORS": {
      return {
        ...state,
        userEmails: [],
        destinationUsers: [],
        bulkRewardState: {
          ...state.bulkRewardState,
          failedEmails: []
        },
        bulkBudgetAssignmentState: {
          assignments: [],
          totalToPay: 0,
          failedEmails: []
        }
      }
    }
    case "SET_BUDGET_CATEGORY": {
      return {
        ...state,
        budgetCategory: action.payload.budgetCategory
      }
    }
    case "SET_CHECK_UNASSIGNED": {
      return {
        ...state,
        checkUnassigned: action.payload.checkUnassigned
      }
    }
    case "SET_SCHEDULE": {
      return {
        ...state,
        schedule: {
          ...state.schedule,
          ...action.payload
        }
      }
    }
    case "SET_CONFIRMATION_MODAL": {
      return {
        ...state,
        modalConfirmIsShown: action.payload.modalConfirmIsShown
      }
    }
    case "SET_TEMPLATE": {
      return {
        ...state,
        rewardTemplate: state.recipientType === DistributeRewardRecipient.MANAGER ? null : action.payload.tag
      }
    }
    case "SET_REWARD_TEMPLATE_TITLE": {
      return {
        ...state,
        rewardTemplateTitle: action.payload.title
      }
    }
    case "SET_RECIPIENT_ERROR": {
      return {
        ...state,
        errors: {
          ...state.errors,
          noRecipientType: action.payload
        }
      }
    }
    case "SET_STEP": {
      return {
        ...state,
        step: action.payload
      }
    }
    default:
      assertUnreachable(action)
  }
}

export default DistributeRewardReducer

function assertUnreachable(branch: never): never {
  throw new Error(`This should not be reachable, likely there is a missing condition or switch case for branch ${branch as string}`)
}
